import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';

const ContactFormPopup = ({ isOpen, onClose, formType = 'general' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const formTitles = {
    inventory: 'Request Inventory',
    brochure: 'Download Brochure',
    general: 'Contact Us',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // ✅ Clean Web3Forms FormData implementation
      const formDataToSend = new FormData();

      formDataToSend.append("access_key", "2c149aad-6814-4041-9d5c-73e74c5009d0");
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("subject", `${formTitles[formType]} - Nava Properties`);
      formDataToSend.append("replyto", formData.email);

      formDataToSend.append("message", `
${formTitles[formType]} Request:

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}

Message:
${formData.message || 'No message provided'}

Request Type: ${formType}
---
Submitted via Nava Properties website
      `);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formDataToSend,
      });

      const data = await response.json();

      if (data.success) {
        console.log('Form submitted:', { ...formData, type: formType });

        setSubmitted(true);

        setTimeout(() => {
          setSubmitted(false);
          onClose();
          setFormData({ name: '', email: '', phone: '', message: '' });
        }, 2000);
      } else {
        alert(data.message || "There was an error submitting your form.");
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Popup */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-yellow-400 to-yellow-600 p-6 rounded-t-3xl flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {formTitles[formType]}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-900" />
                </button>
              </div>

              {/* Content */}
              <div className="p-8">
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">

                    <p className="text-gray-600 mb-6">
                      {formType === 'inventory' && 'Fill in your details and we\'ll email you the complete inventory list within 24 hours.'}
                      {formType === 'brochure' && 'Enter your details to download the project brochure instantly.'}
                      {formType === 'general' && 'Get in touch with us. We\'ll respond within 24 hours.'}
                    </p>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-yellow-500"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-yellow-500"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-yellow-500"
                        placeholder="+971 XX XXX XXXX"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">
                        Message (Optional)
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-yellow-500 resize-none"
                        placeholder="Any specific requirements..."
                      />
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                      <p className="text-sm text-gray-700">
                        <strong>Privacy Guarantee:</strong> We will not call/email/message you without your consent.
                      </p>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold rounded-xl flex items-center justify-center gap-2"
                    >
                      <Send className="w-5 h-5" />
                      Submit Request
                    </button>

                  </form>
                ) : (
                  <motion.div
                    className="py-12 text-center"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>

                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      Request Submitted!
                    </h3>

                    <p className="text-gray-600">
                      We'll get back to you within 24 hours.
                    </p>
                  </motion.div>
                )}
              </div>

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ContactFormPopup;
